import { useEffect, useState } from "react";
import { api, fileUrl, apiError } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { CATEGORY_COLORS, formatDate } from "../lib/categories";
import { toast } from "sonner";
import {
  Building2, Check, X, Trash2, Users, Clock, CheckCircle2, ChevronDown, ChevronRight,
  Pencil, KeyRound, FileText, FileSpreadsheet, User, Loader2, BadgeCheck, Eye, EyeOff, Copy,
} from "lucide-react";

export default function MasterDashboard() {
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [membersMap, setMembersMap] = useState({});
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [editClub, setEditClub] = useState(null);
  const [pwClub, setPwClub] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [savingClub, setSavingClub] = useState(false);
  const [exporting, setExporting] = useState("");
  const [revealed, setRevealed] = useState({});
  const [requests, setRequests] = useState([]);
  const [licenseFee, setLicenseFee] = useState(null);
  const [feeInput, setFeeInput] = useState("");
  const [savingFee, setSavingFee] = useState(false);
  const [licenseMode, setLicenseMode] = useState("online");
  const [activeSeason, setActiveSeason] = useState("");
  const [seasonInput, setSeasonInput] = useState("");
  const [savingMode, setSavingMode] = useState(false);
  const [savingSeason, setSavingSeason] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/clubs").then((res) => setClubs(res.data)).finally(() => setLoading(false));
  };
  const loadRequests = () => {
    api.get("/password-requests").then((res) => setRequests(res.data)).catch(() => {});
  };
  const loadFee = () => {
    api.get("/settings").then((r) => {
      setLicenseFee(r.data.license_fee); setFeeInput(String(r.data.license_fee));
      setLicenseMode(r.data.license_mode);
      setActiveSeason(r.data.active_season); setSeasonInput(r.data.active_season);
    }).catch(() => {});
  };
  useEffect(() => { load(); loadRequests(); loadFee(); }, []);

  const saveMode = async (mode) => {
    setSavingMode(true);
    try {
      const r = await api.patch("/settings/license-mode", { mode });
      setLicenseMode(r.data.license_mode);
      toast.success("Način licenciranja ažuriran");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSavingMode(false); }
  };

  const saveSeason = async () => {
    if (!seasonInput.trim()) { toast.error("Unesite sezonu"); return; }
    setSavingSeason(true);
    try {
      const r = await api.patch("/settings/active-season", { season: seasonInput.trim() });
      setActiveSeason(r.data.active_season);
      toast.success(`Aktivna sezona: ${r.data.active_season}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSavingSeason(false); }
  };

  const saveFee = async () => {
    const amt = parseFloat(feeInput);
    if (!(amt > 0)) { toast.error("Unesite iznos veći od 0"); return; }
    setSavingFee(true);
    try {
      const r = await api.patch("/settings/license-fee", { amount: amt });
      setLicenseFee(r.data.amount);
      toast.success(`Vrijednost licence postavljena: ${Number(r.data.amount).toFixed(2)} €`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSavingFee(false);
    }
  };

  const resolveRequest = async (id) => {
    try { await api.patch(`/password-requests/${id}/resolve`); loadRequests(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const resetFromRequest = (req) => {
    const club = clubs.find((c) => c.id === req.club_id);
    if (club) { setPwClub(club); setNewPw(""); }
    else toast.error("Klub nije pronađen");
  };

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const toggleExpand = async (club) => {
    if (expanded === club.id) { setExpanded(null); return; }
    setExpanded(club.id);
    setLoadingMembers(true);
    try {
      const res = await api.get(`/clubs/${club.id}/members`);
      setMembersMap((prev) => ({ ...prev, [club.id]: res.data }));
    } catch { toast.error("Greška pri učitavanju članova"); }
    finally { setLoadingMembers(false); }
  };

  const toggleLicense = async (clubId, m) => {
    try {
      await api.patch(`/members/${m.id}/license`, { licensed: !m.licensed });
      setMembersMap((prev) => ({
        ...prev,
        [clubId]: prev[clubId].map((x) => (x.id === m.id ? { ...x, licensed: !m.licensed } : x)),
      }));
      toast.success(!m.licensed ? "Licenca odobrena" : "Licenca uklonjena");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSavingClub(true);
    try {
      const { id, name, city, email, phone, website } = editClub;
      await api.put(`/clubs/${id}`, { name, city, email, phone, website });
      toast.success("Podaci kluba ažurirani");
      setEditClub(null);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSavingClub(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setSavingClub(true);
    try {
      await api.patch(`/clubs/${pwClub.id}/password`, { password: newPw });
      toast.success("Lozinka kluba resetovana");
      setPwClub(null); setNewPw("");
      loadRequests();
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSavingClub(false); }
  };

  const exportClub = async (club, fmt) => {
    setExporting(`${club.id}-${fmt}`);
    try {
      const res = await api.get(`/clubs/${club.id}/members/export?format=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${club.name}_clanovi.${fmt === "xlsx" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Izvoz nije uspio"); }
    finally { setExporting(""); }
  };

  const pending = clubs.filter((c) => c.status === "pending");
  const approved = clubs.filter((c) => c.status === "approved");
  const shown = clubs.filter((c) => filter === "all" || c.status === filter);

  const stats = [
    { label: "Ukupno klubova", value: clubs.length, icon: Building2 },
    { label: "Na čekanju", value: pending.length, icon: Clock },
    { label: "Odobreno", value: approved.length, icon: CheckCircle2 },
    { label: "Ukupno članova", value: clubs.reduce((s, c) => s + (c.member_count || 0), 0), icon: Users },
  ];

  const statusBadge = (s) => {
    const map = { pending: "bg-amber-400 text-black", approved: "bg-[hsl(var(--secondary))] text-white", rejected: "bg-muted text-muted-foreground" };
    const label = { pending: "Na čekanju", approved: "Odobren", rejected: "Odbijen" };
    return <Badge className={`rounded-none ${map[s]}`}>{label[s]}</Badge>;
  };

  return (
    <div className="App min-h-screen bg-background">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Master panel · Udruženje plesnih umjetnika i stvaralaca CG</div>
        <div className="flex flex-wrap items-center gap-3 mb-8">
          <h1 className="font-display text-4xl font-black tracking-tighter">Upravljanje klubovima</h1>
          {requests.length > 0 && (
            <button
              onClick={() => document.querySelector('[data-testid="password-requests-panel"]')?.scrollIntoView({ behavior: "smooth", block: "center" })}
              className="inline-flex items-center gap-1.5 bg-destructive text-destructive-foreground text-sm font-bold px-3 py-1.5 rounded-full animate-pulse"
              data-testid="requests-badge"
              title="Novi zahtjevi za reset lozinke"
            >
              <KeyRound className="w-4 h-4" />
              {requests.length} {requests.length === 1 ? "novi zahtjev" : "novih zahtjeva"}
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border mb-10">
          {stats.map((s) => (
            <div key={s.label} className="bg-card p-6" data-testid={`stat-${s.label}`}>
              <s.icon className="w-5 h-5 text-primary mb-3" />
              <div className="font-display text-4xl font-black tracking-tighter">{s.value}</div>
              <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="border-2 border-primary/60 bg-card p-5 mb-8" data-testid="settings-card">
          <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-4">Podešavanja licenciranja i sezone</div>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <Label>Način licenciranja</Label>
              <div className="flex flex-col gap-2 mt-2">
                {[["online", "Online plaćanje (Monri)"], ["manual", "Žiro-račun (ručno)"], ["off", "Bez licenciranja (samo uvid)"]].map(([val, lbl]) => (
                  <button key={val} onClick={() => saveMode(val)} disabled={savingMode}
                    className={`text-sm px-3 py-2 border-2 text-left transition-colors ${licenseMode === val ? "bg-[hsl(var(--secondary))] text-white border-[hsl(var(--secondary))]" : "border-border hover:border-foreground"}`}
                    data-testid={`mode-${val}`}>
                    {licenseMode === val ? "● " : "○ "}{lbl}
                  </button>
                ))}
              </div>
            </div>
            <div className={licenseMode === "off" ? "opacity-40 pointer-events-none" : ""}>
              <Label htmlFor="fee">Vrijednost licence (po članu)</Label>
              <div className="font-display text-3xl font-black tracking-tighter mt-1" data-testid="license-fee-value">{licenseFee != null ? `${Number(licenseFee).toFixed(2)} €` : "—"}</div>
              <div className="flex items-end gap-2 mt-2">
                <Input id="fee" type="number" min="0" step="0.5" className="rounded-none border-2 w-28" value={feeInput} onChange={(e) => setFeeInput(e.target.value)} data-testid="license-fee-input" />
                <Button className="rounded-none bg-primary hover:bg-primary/90 h-10" onClick={saveFee} disabled={savingFee} data-testid="license-fee-save">
                  {savingFee ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sačuvaj"}
                </Button>
              </div>
            </div>
            <div>
              <Label htmlFor="season">Aktivna sezona</Label>
              <div className="font-display text-3xl font-black tracking-tighter mt-1" data-testid="active-season-value">{activeSeason || "—"}</div>
              <div className="flex items-end gap-2 mt-2">
                <Input id="season" className="rounded-none border-2 w-32" value={seasonInput} onChange={(e) => setSeasonInput(e.target.value)} placeholder="2026/2027" data-testid="active-season-input" />
                <Button className="rounded-none bg-primary hover:bg-primary/90 h-10" onClick={saveSeason} disabled={savingSeason} data-testid="active-season-save">
                  {savingSeason ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sačuvaj"}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Nova sezona = prazan spisak; prethodne ostaju u arhivi.</p>
            </div>
          </div>
        </div>

        {requests.length > 0 && (
          <div className="border-2 border-primary bg-primary/5 mb-8" data-testid="password-requests-panel">
            <div className="px-5 py-3 border-b border-primary/30 flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-primary" />
              <span className="font-display font-bold">Zahtjevi za reset lozinke ({requests.length})</span>
            </div>
            <div className="divide-y divide-border">
              {requests.map((r) => (
                <div key={r.id} className="flex flex-wrap items-center gap-3 px-5 py-3 bg-card" data-testid={`request-${r.id}`}>
                  <div className="flex-1 min-w-[180px]">
                    <div className="font-semibold">{r.club_name}</div>
                    <div className="text-xs text-muted-foreground">{r.email}</div>
                  </div>
                  <Button size="sm" className="rounded-none bg-primary hover:bg-primary/90" onClick={() => resetFromRequest(r)} data-testid={`request-reset-${r.id}`}>
                    <KeyRound className="w-4 h-4 mr-1" /> Resetuj lozinku
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-none" onClick={() => resolveRequest(r.id)} data-testid={`request-dismiss-${r.id}`}>Odbaci</Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-2 mb-6 flex-wrap">
          {["all", "pending", "approved", "rejected"].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`text-sm px-4 py-2 border-2 transition-colors ${filter === f ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground"}`}
              data-testid={`filter-${f}`}>
              {{ all: "Svi", pending: "Na čekanju", approved: "Odobreni", rejected: "Odbijeni" }[f]}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-20 text-center text-muted-foreground">Učitavanje...</div>
        ) : shown.length === 0 ? (
          <div className="py-20 text-center text-muted-foreground" data-testid="master-no-clubs">Nema klubova u ovoj kategoriji.</div>
        ) : (
          <div className="border border-border divide-y divide-border">
            {shown.map((c) => (
              <div key={c.id} data-testid={`master-club-${c.id}`}>
                <div
                  className={`dove-card flex items-center gap-4 p-4 transition-colors ${c.status === "approved" ? "cursor-pointer" : ""}`}
                  onClick={() => c.status === "approved" && toggleExpand(c)}
                  data-testid={`expand-${c.id}`}
                  title={c.status === "approved" ? "Prikaži članove" : "Klub nije odobren"}
                >
                  <span className={`shrink-0 ${c.status === "approved" ? "text-foreground" : "text-muted-foreground/30"}`}>
                    {expanded === c.id ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </span>
                  <div className="w-12 h-12 border border-border bg-muted flex items-center justify-center overflow-hidden shrink-0">
                    {c.logo_path ? <img src={fileUrl(c.logo_path)} alt="" className="w-full h-full object-cover" /> : <Building2 className="w-5 h-5 text-muted-foreground" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-lg flex items-center gap-2 flex-wrap">
                      {c.serial_no && <span className="text-primary">#{c.serial_no}</span>}
                      {c.name}
                      {statusBadge(c.status)}
                    </div>
                    <div className="text-sm text-muted-foreground truncate">
                      {c.city} · {c.email} · {c.phone} · {c.member_count || 0} članova
                    </div>
                    <div className="text-[11px] mt-1 flex items-center gap-1.5 flex-wrap" data-testid={`credentials-${c.id}`} onClick={(e) => e.stopPropagation()}>
                      <span className="uppercase tracking-wider text-muted-foreground">Ulaz:</span>
                      <span className="font-mono font-semibold bg-muted px-1.5 py-0.5">{c.login}</span>
                      <span className="uppercase tracking-wider text-muted-foreground ml-2">Lozinka:</span>
                      <span className="font-mono font-semibold bg-muted px-1.5 py-0.5" data-testid={`password-${c.id}`}>
                        {revealed[c.id] ? (c.password || "nije zabilježena — resetuj") : "••••••••"}
                      </span>
                      <button
                        type="button"
                        onClick={() => setRevealed((p) => ({ ...p, [c.id]: !p[c.id] }))}
                        className="text-muted-foreground hover:text-foreground p-1"
                        title={revealed[c.id] ? "Sakrij" : "Prikaži lozinku"}
                        data-testid={`reveal-password-${c.id}`}
                      >
                        {revealed[c.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                      {c.password && (
                        <button
                          type="button"
                          onClick={() => { navigator.clipboard.writeText(`${c.login} / ${c.password}`); toast.success("Pristupni podaci kopirani"); }}
                          className="text-muted-foreground hover:text-foreground p-1"
                          title="Kopiraj ulaz i lozinku"
                          data-testid={`copy-credentials-${c.id}`}
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                    {c.status !== "approved" && (
                      <Button size="sm" className="rounded-none bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--secondary))]/90" onClick={() => act(() => api.patch(`/clubs/${c.id}/approve`), "Klub odobren")} data-testid={`approve-${c.id}`}>
                        <Check className="w-4 h-4 mr-1" /> Odobri
                      </Button>
                    )}
                    {c.status === "pending" && (
                      <Button size="sm" variant="outline" className="rounded-none border-2" onClick={() => act(() => api.patch(`/clubs/${c.id}/reject`), "Klub odbijen")} data-testid={`reject-${c.id}`}>
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" className="rounded-none h-8 w-8" onClick={() => setEditClub({ ...c })} data-testid={`edit-club-${c.id}`} title="Izmijeni klub">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="rounded-none h-8 w-8" onClick={() => { setPwClub(c); setNewPw(""); }} data-testid={`password-club-${c.id}`} title="Reset lozinke">
                      <KeyRound className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="icon" variant="ghost" className="rounded-none h-8 w-8 text-primary" data-testid={`delete-club-${c.id}`} title="Obriši klub">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-none border-2">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Obrisati klub?</AlertDialogTitle>
                          <AlertDialogDescription>Klub "{c.name}" i svi njegovi članovi biće trajno obrisani.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-none">Otkaži</AlertDialogCancel>
                          <AlertDialogAction className="rounded-none bg-primary hover:bg-primary/90" onClick={() => act(() => api.delete(`/clubs/${c.id}`), "Klub obrisan")}>Obriši</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>

                {/* Expanded members panel */}
                {expanded === c.id && (
                  <div className="bg-muted/30 border-t border-border p-4" data-testid={`members-panel-${c.id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Članovi · uključi licencu (plavo)</div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="rounded-none border-2 h-8" onClick={() => exportClub(c, "pdf")} disabled={exporting === `${c.id}-pdf`} data-testid={`export-pdf-${c.id}`}>
                          {exporting === `${c.id}-pdf` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1" />} PDF
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-none border-2 h-8" onClick={() => exportClub(c, "xlsx")} disabled={exporting === `${c.id}-xlsx`} data-testid={`export-excel-${c.id}`}>
                          {exporting === `${c.id}-xlsx` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />} Excel
                        </Button>
                      </div>
                    </div>
                    {loadingMembers && !membersMap[c.id] ? (
                      <div className="py-6 text-center text-sm text-muted-foreground">Učitavanje članova...</div>
                    ) : (membersMap[c.id] || []).length === 0 ? (
                      <div className="py-6 text-center text-sm text-muted-foreground">Ovaj klub još nema članova.</div>
                    ) : (
                      <div className="divide-y divide-border border border-border bg-background">
                        {(membersMap[c.id] || []).map((m) => (
                          <div key={m.id} className={`flex items-center gap-3 px-3 py-2 transition-colors ${m.licensed ? "bg-sky-500/15" : "bg-white/5"}`} data-testid={`master-member-${m.id}`}>
                            <span className="font-display font-bold text-primary w-6">{String(m.serial_no).padStart(2, "0")}</span>
                            <div className="w-8 h-8 border border-border bg-muted overflow-hidden flex items-center justify-center shrink-0">
                              {m.photo_path ? <img src={fileUrl(m.photo_path)} alt="" className="w-full h-full object-cover" /> : <User className="w-3.5 h-3.5 text-muted-foreground" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-sm truncate">{m.last_name} {m.first_name}</div>
                              <div className="text-xs text-muted-foreground">{formatDate(m.dob)} · {m.age} god.</div>
                            </div>
                            <span className={`hidden sm:inline-block text-xs font-semibold px-2 py-0.5 border ${CATEGORY_COLORS[m.age_category] || CATEGORY_COLORS["Van kategorije"]}`}>{m.age_category}</span>
                            <div className="flex items-center gap-2 shrink-0">
                              {m.licensed && <BadgeCheck className="w-4 h-4 text-sky-600" />}
                              <span className="text-xs text-muted-foreground hidden sm:inline">Licenca</span>
                              <Switch checked={m.licensed} onCheckedChange={() => toggleLicense(c.id, m)} data-testid={`license-switch-${m.id}`} />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit club dialog */}
      <Dialog open={!!editClub} onOpenChange={(o) => !o && setEditClub(null)}>
        <DialogContent className="rounded-none border-2 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tighter">Izmijeni klub</DialogTitle>
            <DialogDescription>Ažurirajte podatke kluba članice.</DialogDescription>
          </DialogHeader>
          {editClub && (
            <form onSubmit={saveEdit} className="space-y-4">
              <div>
                <Label>Ime kluba</Label>
                <Input className="rounded-none border-2 mt-1.5" value={editClub.name || ""} onChange={(e) => setEditClub({ ...editClub, name: e.target.value })} data-testid="edit-club-name" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Grad</Label>
                  <Input className="rounded-none border-2 mt-1.5" value={editClub.city || ""} onChange={(e) => setEditClub({ ...editClub, city: e.target.value })} data-testid="edit-club-city" />
                </div>
                <div>
                  <Label>Telefon</Label>
                  <Input className="rounded-none border-2 mt-1.5" value={editClub.phone || ""} onChange={(e) => setEditClub({ ...editClub, phone: e.target.value })} data-testid="edit-club-phone" />
                </div>
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" className="rounded-none border-2 mt-1.5" value={editClub.email || ""} onChange={(e) => setEditClub({ ...editClub, email: e.target.value })} data-testid="edit-club-email" />
              </div>
              <div>
                <Label>Web stranica</Label>
                <Input className="rounded-none border-2 mt-1.5" value={editClub.website || ""} onChange={(e) => setEditClub({ ...editClub, website: e.target.value })} data-testid="edit-club-website" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={savingClub} className="w-full rounded-none bg-primary hover:bg-primary/90 h-11" data-testid="edit-club-save">
                  {savingClub ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sačuvaj izmjene"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!pwClub} onOpenChange={(o) => !o && setPwClub(null)}>
        <DialogContent className="rounded-none border-2 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tighter">Reset lozinke</DialogTitle>
            <DialogDescription>Postavite novu lozinku za klub "{pwClub?.name}".</DialogDescription>
          </DialogHeader>
          <form onSubmit={savePassword} className="space-y-4">
            <div>
              <Label>Nova lozinka</Label>
              <Input type="text" className="rounded-none border-2 mt-1.5" value={newPw} onChange={(e) => setNewPw(e.target.value)} required minLength={6} placeholder="min. 6 karaktera" data-testid="reset-password-input" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={savingClub} className="w-full rounded-none bg-primary hover:bg-primary/90 h-11" data-testid="reset-password-save">
                {savingClub ? <Loader2 className="w-4 h-4 animate-spin" /> : "Resetuj lozinku"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
